<template>
  <div class="maps-container">
    <div v-if="!has_location" class="center-on-map bold">
      {{ $t("common.labels.noLocation") }}
    </div>
    <div
      :class="{
        'cover-map': !has_location,
        'maps-container': true,
      }"
    >
      <div class="slider-con" v-if="map_click">
        <v-slider
          class="text-caption"
          v-model="radius_final"
          max="1000"
          min="0"
          thumb-color="primaryOutline"
          thumb-label="always"
          thumb-size="30"
          step="50"
          vertical
        >
          <template v-slot:prepend>
            <v-icon small color="primaryGrey"> mdi-minus </v-icon>
          </template>
          <template v-slot:append>
            <div>
              <div class="py-1">Radius</div>
              <div class="text-center">
                <v-icon small color="primaryGrey"> mdi-plus </v-icon>
              </div>
            </div>
          </template>
        </v-slider>
      </div>
      <div
        v-if="!hide_legends && map"
        class="legends d-flex font-weight-bold text-caption justify-space-around"
        :style="{ backgroundColor: theme_background }"
      >
        <div class="titleLight--text" v-for="(item, idx) in legends" :key="idx">
          <v-avatar v-if="!item.icon" :color="item.color" size="12" />
          <v-icon v-else :color="item.color" small>
            {{ item.icon }}
          </v-icon>
          {{ item.label }}
        </div>
      </div>
      <div class="categories-div" v-if="options && options.categories">
        <div v-for="(item, idx) in options.categories" :key="idx">
          <v-checkbox
            class="mt-0"
            small
            hide-details
            dense
            :value="item.value"
            v-model="selectedCat"
            :label="`${item.label}`"
            @change="$emit('filter', selectedCat)"
          ></v-checkbox>
        </div>
      </div>
      <div class="cluster-div" v-if="!heat_map_on && items && items.length >= 3">
        <v-checkbox
          class="mt-0 text-h6"
          large
          hide-details
          dense
          v-model="updated_clusterMarkers"
          label="Cluster"
        ></v-checkbox>
      </div>
      <div class="cluster-div font-weight-bold" v-if="show_description">
        <div class="pt-2 px-2">
          {{ selected_region.loc
          }}<span class="primary--text font-weight-normal"> : {{ selected_region.count }} {{ options.suffix }}</span>
        </div>
        <v-divider v-if="selected_region.count > 0" class="my-2"></v-divider>
        <div v-if="selected_region.count > 0" class="pb-2 px-2 text-caption text-uppercase">
          <div class="text-caption" v-for="(dev, idx) in Object.keys(selected_region.status)" :key="`status_${idx}`">
            <div class="primaryOutline--text font-weight-bold">
              {{ dev }}:<span class="primary--text font-weight-normal">{{
                selected_region.status[dev].devices
                  ? selected_region.status[dev].devices.length
                  : selected_region.status[dev]
              }}</span>
            </div>
            <div v-if="!show_total">
              <div
                class="primary--text px-3 text-caption text-lowercase"
                v-for="(device, idx) in selected_region.status[dev].devices"
                :key="`devices_${idx}`"
              >
                <v-icon v-if="deviceTypeConfig(dev, idx)" small :color="deviceTypeConfig(dev, idx).color">
                  {{ deviceTypeConfig(dev, idx).icon }}
                </v-icon>
                <v-icon v-else x-small color="primary">mdi-help</v-icon>
                {{ device }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div v-if="msg && items.length == 0" class="info-message font-weight-bold text-caption justify-space-around">
        <span>{{ msg }}</span>
      </div>
      <div id="map" ref="map" :style="{ 'min-height': height }"></div>
      <!-- Google clears #map and moves info window content into its own DOM, so Vue-managed
           children must live outside it or Vue's DOM patching throws (insertBefore NotFoundError) -->
      <div class="map-overlays">
        <MapMarkerVT
          v-if="!isHeatMap"
          :no_clusters="heat_map_on"
          :items="updated_heat_map_on ? [] : items"
          :options="options"
          :show_info_window="!show_info_window"
          :clusterMarkers="updated_clusterMarkers"
          @show_infowindow="show_infowindow"
          @close_infowindow="close_infowindow"
          @marker_click="add_circle"
          @marker_clicked="marker_clicked"
          @show_group="show_group"
          @close_group="close_group"
          :reset_zoom="!heat_map_on"
          :liveStream="liveStream"
          :initial_lat_lng="initial_lat_lng"
          :initial_zoom="initial_zoom"
        ></MapMarkerVT>
        <MapInfoWindow
          v-if="showInfoWindow && !show_info_window"
          :lat="infoWindowItem[options.lat]"
          :lng="infoWindowItem[options.lng]"
          disableAutoPan
          @close_infowindow="close_infowindow"
          :options="options"
        >
          <MapInfoWindowPowerdataTemplate
            v-if="options.datatype == 'powerdata'"
            :infoWindowItem="infoWindowItem"
            :infoWindowItemsConfig="infoWindowItemsConfig"
            :options="options"
          ></MapInfoWindowPowerdataTemplate>
          <MapInfoWindowTemplate v-else :infoWindowItem="infoWindowItem" :options="options"></MapInfoWindowTemplate>
        </MapInfoWindow>
        <MapInfoWindow
          v-if="groupWindow"
          :key="groupWindow.key"
          :lat="groupWindow.position.lat()"
          :lng="groupWindow.position.lng()"
          disableAutoPan
          @close_infowindow="close_group"
        >
          <div class="group-list">
            <div class="group-list-header font-weight-bold primary--text">
              <v-icon small color="primary">mdi-map-marker-multiple</v-icon>
              {{ groupWindow.total }}
            </div>
            <v-divider class="my-1"></v-divider>
            <div class="group-list-items">
              <div
                v-for="(entry, idx) in groupWindow.entries"
                :key="`group_item_${idx}`"
                class="group-list-item text-caption primary--text"
                @click="group_item_click(entry.item)"
              >
                <span class="group-list-dot" :style="{ background: entry.color }"></span>
                {{ entry.item[options.infoText] || entry.item[options.id] }}
                <span v-if="options.label && entry.item[options.label] != null" class="primaryOutline--text">
                  ({{ entry.item[options.label] }})
                </span>
              </div>
            </div>
          </div>
        </MapInfoWindow>
      </div>
    </div>
  </div>
</template>

<script>
import MapMarkerVT from "./MapMarkerVT";
import MapInfoWindow from "./MapInfoWindow";
import MapInfoWindowTemplate from "./MapInfoWindowTemplate";
import MapInfoWindowPowerdataTemplate from "./MapInfoWindowPowerdataTemplate";
import { content } from "../../config/config";

export default {
  name: "MapsVT",
  components: {
    MapMarkerVT,
    MapInfoWindow,
    MapInfoWindowTemplate,
    MapInfoWindowPowerdataTemplate,
  },
  props: {
    items: Array,
    center: Object,
    height: String,
    showRoute: Boolean,
    start: String,
    end: String,
    options: Object,
    no_info_window: Boolean,
    radius: Number,
    map_click: Boolean,
    hide_legends: Boolean,
    legends: Array,
    new_item: Object,
    msg: String,
    selected: Array,
    clusterMarkers: Boolean,
    heat_map_on: Boolean,
    show_total: Boolean,
    infoWindowItemsConfig: Array,
    update_map: Boolean,
    liveStream: {
      type: Boolean,
      default: false,
    },
    isHeatMap: {
      type: Boolean,
      default: false,
    },
  },
  watch: {
    center: function (newVal) {
      this.map && this.map.setCenter(newVal);
    },
    start: function (newVal) {
      newVal && this.init_directions();
    },
    map_click: function (newVal) {
      this.marker && this.marker.setMap(null) && window.google.maps.event.clearInstanceListeners(this.marker);
      newVal ? this.add_click_listner() : this.click_listner && this.remove_click_listner();
    },
    items: function (newVal) {
      if (this.liveStream && this.has_rendred) return; // To avoid re-rendering of map when live stream is enabled
      if (newVal && this.showRoute && !this.start) this.map = this.init_lines();
      if (newVal && !this.showRoute && !this.start) this.map = this.init_marker();
      if (newVal && this.heat_map_on) {
        this.addHeatMap();
      }
      if (this.map_click) {
        this.add_click_listner();
      }
      if (this.isHeatMap) this.setHeatMap();
      setTimeout(() => {
        this.has_rendred = true;
      }, 5000);
    },
    radius: function (newVal) {
      this.radius_final = newVal;
    },
    selected: function (newVal) {
      this.selectedCat = newVal;
    },
    clusterMarkers: function (newVal) {
      this.updated_clusterMarkers = newVal;
    },
    heat_map_on: function (newVal) {
      this.updated_heat_map_on = newVal;
    },
    radius_final: function (newVal) {
      this.circle && this.circle.setRadius(newVal);
    },
    new_item: function (newVal) {
      this.pseudo_marker &&
        this.pseudo_marker.setMap(null) &&
        window.google.maps.event.clearInstanceListeners(this.pseudo_marker);
      if (newVal) this.add_pseudo_marker(newVal);
    },
    "$vuetify.theme.dark": {
      immediate: true,
      handler(newVal) {
        this.map_theme(newVal);
      },
    },
  },
  data: function () {
    return {
      map: null,
      showInfoWindow: false,
      infoWindowItem: {},
      circle: null,
      click_listner: null,
      radius_final: 0,
      marker: null,
      pseudo_marker: null,
      selectedCat: null,
      updated_clusterMarkers: true,
      updated_heat_map_on: false,
      groupWindow: null,
      features: null,
      lightMapStyle: null,
      regions: null,
      darkMapStyle: null,
      heatmap: null,
      show_description: false,
      selected_region: null,
      device_types_config: null,
      has_rendred: false,
      initial_lat_lng: null,
      initial_zoom: null,
    };
  },
  created() {
    this.device_types_config = content.DeviceTypes;
  },
  mounted() {
    this.initial_lat_lng = this.$store.getters.getInitialLatLng;
    this.initial_zoom = this.$store.getters.getInitialMapZoom;
    this.map = this.showRoute ? (this.start ? this.init_directions() : this.init_lines()) : this.init_marker();
    this.updated_heat_map_on = this.heat_map_on;
    if (this.heat_map_on) {
      this.addHeatMap();
    }
    this.selectedCat = this.selected;
    this.radius_final = this.radius || 0;
    if (this.map_click) {
      this.add_click_listner();
    }
    this.assign_styles();
    this.map_theme();
    this.initilizeLocation();
    if (this.isHeatMap) this.setHeatMap();
    // console.log("test", this.map);
    // setTimeout(() => {
    //   console.log("test2", this.map);
    //   window.google.maps.event.trigger(this.map, "resize");
    // }, 5000);
  },
  methods: {
    initilizeLocation() {
      this.map.setCenter(this.initial_lat_lng);
      this.map.setZoom(this.initial_zoom);
    },
    fetchJSON(url) {
      return fetch(url).then(function (response) {
        return response.json();
      });
    },
    async addHeatMap() {
      let self = this;
      this.selected_region = null;
      // Remove existing features from map
      if (this.map && !this.update_map) {
        this.map.data.forEach(function (feature) {
          self.map.data.remove(feature);
        });
      }

      if (this.items && this.items.length > 0 && this.map) {
        // Load GeoJson data(Ireland) and add to map
        if (this.map && !this.update_map) this.map.data.loadGeoJson("../assets/data/planner_group.geojson");
        let max = 0,
          min = 0;
        let regions_device_count = {};
        // Group data by country, county/state and status and count devices in each region
        regions_device_count = this.items.reduce((acc, curr) => {
          let label = curr[this.options.label];
          let current_planner_group = curr[this.options.adminArea] ? curr[this.options.adminArea].toLowerCase() : "";
          let status;
          if (!self.show_total) {
            status = !this.options.isStatusBoolean
              ? curr[this.options.status] == "Installed"
                ? curr[this.options.status]
                : "Not Installed"
              : curr[this.options.status]
                ? "Active"
                : "Inactive";
          } else {
            status = curr[this.options.status] > 1 ? "Overloaded" : "Normal";
          }
          let type = curr[this.options.deviceType];
          acc[current_planner_group] = acc[current_planner_group] ? acc[current_planner_group] : {};
          acc[current_planner_group][status] = acc[current_planner_group][status]
            ? acc[current_planner_group][status]
            : {};
          acc[current_planner_group]["total"] = acc[current_planner_group]["total"]
            ? acc[current_planner_group]["total"] + 1
            : 1;
          acc[current_planner_group][status]["count"] = acc[current_planner_group][status]["count"]
            ? acc[current_planner_group][status]["count"] + 1
            : 1;
          acc[current_planner_group][status]["devices"] = acc[current_planner_group][status]["devices"]
            ? acc[current_planner_group][status]["devices"].concat(label)
            : [label];
          acc[current_planner_group][status]["type"] = acc[current_planner_group][status]["type"]
            ? acc[current_planner_group][status]["type"].concat(type)
            : [type];

          if (max < acc[current_planner_group]["total"]) {
            max = acc[current_planner_group]["total"];
          }
          if (min > acc[current_planner_group]["total"]) {
            min = acc[current_planner_group]["total"];
          }
          return acc;
        }, {});
        // Set style for each region based on device count
        this.map.data.setStyle(function (feature) {
          let planner_group = feature.getProperty("NAME").toLowerCase(); // feature.getProperty("NAME_1");
          let count =
            regions_device_count && regions_device_count[planner_group] && regions_device_count[planner_group]["total"]
              ? regions_device_count[planner_group]["total"]
              : 0;
          if (!self.show_total)
            return {
              fillColor: count == 0 ? "#e0e0e0" : "#1b428b",
              strokeWeight: 1,
              fillOpacity: count == 0 ? 0.8 : (count - min) / (max - min),
            };
          else {
            let overlaoded_devices_perc =
              regions_device_count &&
              regions_device_count[planner_group] &&
              regions_device_count[planner_group]["Overloaded"]
                ? (regions_device_count[planner_group]["Overloaded"].count / count) * 100
                : 0;
            let color = self.getOverlaodColor(overlaoded_devices_perc);
            return {
              fillColor: count == 0 ? "#e0e0e0" : color,
              strokeWeight: 1,
              fillOpacity: 0.8,
            };
          }
        });
        // Add event listeners for mouseover and mouseout
        this.map.data.addListener("mouseover", function (event) {
          let county_state = event.feature.getProperty("NAME") ? event.feature.getProperty("NAME").toLowerCase() : "";
          this.map.data.overrideStyle(event.feature, { fillColor: "#1e8ecc", fillOpacity: 0.5 });
          let { total, ...rest } = regions_device_count[county_state]
            ? regions_device_count[county_state]
            : { total: 0 };
          self.selected_region = {
            loc: county_state,
            status: rest,
            count: total ? total : 0,
          };
          self.show_description = true;
        });
        this.map.data.addListener("mouseout", function (event) {
          this.map.data.revertStyle(event.feature);
          self.selected_region = null;
          self.show_description = false;
        });
      }
    },
    getOverlaodColor(perc) {
      if (0 <= perc && perc <= 10) {
        return "#bbc492";
      } else if (10 < perc && perc <= 25) {
        return "#ffa500";
      } else if (25 < perc && perc <= 50) {
        return "#c63e12";
      } else if (50 < perc) {
        return "#e14615";
      }
    },
    remove_rating_layer() {
      this.map.data.remove(this.features[0]);
    },
    add_click_listner() {
      this.click_listner = this.map.addListener("click", (mapsMouseEvent) => {
        this.circle && this.circle.setMap(null) && window.google.maps.event.clearInstanceListeners(this.circle);
        this.circle = new window.google.maps.Circle({
          radius: this.radius_final,
          center: mapsMouseEvent.latLng,
          map: this.map,
          fillColor: "#1e8ecc",
          fillOpacity: 0.2,
          strokeColor: "#1e8ecc",
          strokeOpacity: 0.6,
        });
        this.add_marker(mapsMouseEvent.latLng);
        this.map.setZoom(15);
        this.map.setCenter(mapsMouseEvent.latLng);
        this.circle.addListener("click", (circleMouseEvent) => {
          this.$emit("map-click", [circleMouseEvent.latLng.toJSON(), this.radius_final]);
        });
        this.circle.addListener("mouseover", () => {
          this.map.getDiv().setAttribute("title", "Click to add new location");
        });
        this.circle.addListener("mouseout", () => {
          this.map.getDiv().removeAttribute("title");
        });
      });
    },
    add_marker(position) {
      this.marker && this.marker.setMap(null) && window.google.maps.event.clearInstanceListeners(this.marker);
      this.marker = new window.google.maps.Marker({
        position: position,
        map: this.map,
        draggable: true,
        animation: window.google.maps.Animation.DROP,
      });
      this.marker.addListener("dragend", (event) => {
        this.add_circle(event.latLng);
      });
      this.marker.addListener("click", (event) => {
        this.$emit("map-click", [event.latLng.toJSON(), this.radius_final]);
      });
      this.marker.addListener("rightclick", () => {
        this.marker.setMap(null);
        this.circle && this.circle.setMap(null);
      });
    },
    add_circle(position) {
      this.circle && this.circle.setMap(null) && window.google.maps.event.clearInstanceListeners(this.circle);
      this.circle = new window.google.maps.Circle({
        radius: this.radius_final,
        center: position,
        map: this.map,
        fillColor: "#1e8ecc",
        fillOpacity: 0.2,
        strokeColor: "#1e8ecc",
        strokeOpacity: 0.6,
        title: "Click to add new location",
      });
      if (this.marker) this.marker.setPosition(position);
      else this.add_marker(position);
      this.map.setZoom(15);
      this.map.setCenter(position);
      this.circle.addListener("click", (circleMouseEvent) => {
        this.$emit("map-click", [circleMouseEvent.latLng.toJSON(), this.radius_final]);
      });
      this.circle.addListener("mouseover", () => {
        this.map.getDiv().setAttribute("title", "Click to add new location");
      });
      this.circle.addListener("mouseout", () => {
        this.map.getDiv().removeAttribute("title");
      });
    },
    marker_clicked(item) {
      this.$emit("marker_clicked", item);
    },
    remove_click_listner() {
      window.google.maps.event.clearListeners(this.map, "click");
    },
    assign_styles() {
      this.lightMapStyle = [
        {
          featureType: "administrative",
          elementType: "labels",
          stylers: [{ visibility: "on" }],
        },
        {
          featureType: "poi",
          elementType: "labels",
          stylers: [{ visibility: "on" }],
        },
        {
          featureType: "water",
          elementType: "labels",
          stylers: [{ visibility: "on" }],
        },
      ];

      this.darkMapStyle = [
        { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.stroke", stylers: [{ color: "#242f3e" }] },
        { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
        {
          featureType: "administrative.locality",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "poi",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "poi.park",
          elementType: "geometry",
          stylers: [{ color: "#263c3f" }],
        },
        {
          featureType: "poi.park",
          elementType: "labels.text.fill",
          stylers: [{ color: "#6b9a76" }],
        },
        {
          featureType: "road",
          elementType: "geometry",
          stylers: [{ color: "#38414e" }],
        },
        {
          featureType: "road",
          elementType: "geometry.stroke",
          stylers: [{ color: "#212a37" }],
        },
        {
          featureType: "road",
          elementType: "labels.text.fill",
          stylers: [{ color: "#9ca5b3" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry",
          stylers: [{ color: "#746855" }],
        },
        {
          featureType: "road.highway",
          elementType: "geometry.stroke",
          stylers: [{ color: "#1f2835" }],
        },
        {
          featureType: "road.highway",
          elementType: "labels.text.fill",
          stylers: [{ color: "#f3d19c" }],
        },
        {
          featureType: "transit",
          elementType: "geometry",
          stylers: [{ color: "#2f3948" }],
        },
        {
          featureType: "transit.station",
          elementType: "labels.text.fill",
          stylers: [{ color: "#d59563" }],
        },
        {
          featureType: "water",
          elementType: "geometry",
          stylers: [{ color: "#17263c" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.fill",
          stylers: [{ color: "#515c6d" }],
        },
        {
          featureType: "water",
          elementType: "labels.text.stroke",
          stylers: [{ color: "#17263c" }],
        },
      ];
    },
    map_theme() {
      const darkMapStyle = new window.google.maps.StyledMapType(this.darkMapStyle, { name: "Dark" });
      const lightMapStyle = new window.google.maps.StyledMapType(this.lightMapStyle, { name: "Light" });

      if (this.map) {
        this.map.mapTypes.set("dark", darkMapStyle);
        this.map.mapTypes.set("light", lightMapStyle);

        this.map.setMapTypeId(this.$vuetify.theme.dark ? "dark" : "light");
      }
    },
    init_directions() {
      var icons = {
        start: new window.google.maps.MarkerImage("http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png"),
        end: new window.google.maps.MarkerImage(
          "http://maps.google.com/mapfiles/kml/shapes/placemark_circle_highlight.png"
        ),
      };
      let directionsService = new window.google.maps.DirectionsService();
      let directionsRenderer = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true,
      });
      var map = new window.google.maps.Map(this.$refs["map"], {
        zoom: 12,
        mapTypeControl: true,
        streetViewControl: true,
        gestureHandling: "greedy",
        center: this.initial_lat_lng,
        //styles: this.themeBackground,
        fullscreenControlOptions: {
          position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
        },
        mapTypeControlOptions: {
          style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: window.google.maps.ControlPosition.BOTTOM_CENTER,
        },
      });
      let request = {
        origin: this.route_start,
        destination: this.route_end,
        travelMode: "DRIVING",
      };
      directionsRenderer.setMap(map);
      directionsService.route(request, function (result, status) {
        if (status == "OK") {
          directionsRenderer && directionsRenderer.setDirections(result);
          var leg = result.routes[0].legs[0];
          new window.google.maps.Marker({
            position: leg.start_location,
            map: map,
            icon: icons.start,
            title: "Origin",
          });
          new window.google.maps.Marker({
            position: leg.end_location,
            map: map,
            icon: icons.end,
            title: "Destination",
          });
        }
      });
      return map;
    },
    init_lines() {
      var map = new window.google.maps.Map(this.$refs["map"], {
        zoom: 14,
        center: this.initial_lat_lng,
        mapTypeId: "terrain",
        gestureHandling: "greedy",
        fullscreenControlOptions: {
          position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
        },
        mapTypeControlOptions: {
          style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: window.google.maps.ControlPosition.BOTTOM_CENTER,
        },
      });
      // Define a symbol using a predefined path (an arrow)
      // supplied by the Google Maps JavaScript API.
      const lineSymbol = {
        path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
        strokeColor: "#f08080",
      };
      var total_items = this.items.length;
      for (let i = 0; i < total_items; i++) {
        if (i != 0) {
          new window.google.maps.Polyline({
            path: [
              new window.google.maps.LatLng(this.items[i - 1][this.options.lat], this.items[i - 1][this.options.lng]),
              new window.google.maps.LatLng(this.items[i][this.options.lat], this.items[i][this.options.lng]),
            ],
            strokeColor: "#1b428b",
            strokeOpacity: 0.9,
            icons: [
              {
                icon: lineSymbol,
                offset: "50%",
              },
            ],
            map: map,
          });
        }
      }
      return map;
    },
    init_route() {
      var icons = {
        start: new window.google.maps.MarkerImage("http://maps.google.com/mapfiles/kml/shapes/placemark_circle.png"),
        end: new window.google.maps.MarkerImage(
          "http://maps.google.com/mapfiles/kml/shapes/placemark_circle_highlight.png"
        ),
      };
      let directionsService = new window.google.maps.DirectionsService();
      let directionsRenderer = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true,
      });
      var map = new window.google.maps.Map(this.$refs["map"], {
        zoom: 12,
        mapTypeControl: true,
        gestureHandling: "greedy",
        streetViewControl: true,
        center: this.initial_lat_lng,
        fullscreenControlOptions: {
          position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
        },
        mapTypeControlOptions: {
          style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
          position: window.google.maps.ControlPosition.BOTTOM_CENTER,
        },
      });
      directionsRenderer.setMap(map);
      const waypts = [];
      const labels = [];
      var total_items = this.items.length;
      for (let i = 1; i < total_items - 1; i++) {
        labels.push(this.items[i][this.options.label]);
        waypts.push({
          location: new window.google.maps.LatLng(this.items[i][this.options.lat], this.items[i][this.options.lng]),
          stopover: true,
        });
      }
      if (this.items && this.items.length > 0)
        directionsService
          .route({
            origin: `${this.items[0][this.options.lat]},
            ${this.items[total_items - 1][this.options.lng]}`,
            destination: `${this.items[0][this.options.lat]},
            ${this.items[total_items - 1][this.options.lng]}`,
            waypoints: waypts,
            optimizeWaypoints: true,
            travelMode: window.google.maps.TravelMode.DRIVING,
          })
          .then((response) => {
            directionsRenderer.setDirections(response);
            var leg = response.routes[0].legs[0];
            waypts.forEach((elem, indx) => {
              new window.google.maps.Marker({
                position: elem.location,
                map: map,
                icon: icons.start,
                title: `${labels[indx]}`,
              });
            });
            new window.google.maps.Marker({
              position: leg.start_location,
              map: map,
              icon: icons.start,
              title: `${labels[0] ? labels[total_items - 1] : "Unknown"}`,
            });
            new window.google.maps.Marker({
              position: leg.end_location,
              map: map,
              icon: icons.end,
              title: `${labels[total_items - 1] ? labels[total_items - 1] : "Unknown"}`,
            });
          })
          .catch((e) => console.log(e));
      return map;
    },
    add_pseudo_marker(obj) {
      const myLatLng = { lat: obj[this.options.lat], lng: obj[this.options.lng] };
      this.pseudo_marker = new window.google.maps.Marker({
        position: myLatLng,
        map: this.map,
        title: obj.FullName,
      });
      this.map.setZoom(8);
      this.map.panTo(myLatLng);
      // this.map.setCenter(myLatLng);
    },
    init_marker() {
      let self = this;
      this.map &&
        !this.update_map &&
        this.map.data.forEach(function (feature) {
          self.map.data.remove(feature);
        });
      if (this.center && this.center.lat() && this.center.lng()) {
        return (
          this.map ||
          new window.google.maps.Map(this.$refs["map"], {
            zoom: 12,
            mapTypeControl: true,
            streetViewControl: false,
            center: this.center,
            gestureHandling: "greedy",
            fullscreenControlOptions: {
              position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
            },
            mapTypeControlOptions: {
              style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
              position: !this.heat_map_on ? window.google.maps.ControlPosition.BOTTOM_CENTER : null,
            },
          })
        );
      } else {
        return (
          this.map ||
          new window.google.maps.Map(this.$refs["map"], {
            zoom: 12,
            mapTypeControl: true,
            gestureHandling: "greedy",
            streetViewControl: false,
            fullscreenControlOptions: {
              position: window.google.maps.ControlPosition.BOTTOM_RIGHT,
            },
            mapTypeControlOptions: {
              style: window.google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
              position: !this.heat_map_on ? window.google.maps.ControlPosition.BOTTOM_CENTER : null,
            },
          })
        );
      }
    },
    getMap(callback) {
      let vm = this;
      function checkForMap() {
        if (vm.map) callback(vm.map);
        else setTimeout(checkForMap, 200);
      }
      checkForMap();
    },
    // Opened on marker hover, so it must not move the map
    show_infowindow(arr) {
      this.showInfoWindow = false;
      this.infoWindowItem = this.items.filter((item) => {
        return item[this.options.id] == arr[0];
      })[0];
      this.showInfoWindow = true;
    },
    close_infowindow() {
      this.showInfoWindow = false;
    },
    show_group({ entries, total, position }) {
      if (this.groupWindow && this.groupWindow.position.equals(position)) return;
      this.groupWindow = { entries, total, position, key: position.toUrlValue() };
    },
    close_group() {
      this.groupWindow = null;
    },
    group_item_click(item) {
      const id = item[this.options.id] != null ? item[this.options.id].toString() : null;
      const position = new window.google.maps.LatLng(item[this.options.lat], item[this.options.lng]);
      this.close_group();
      // Same behaviour as clicking a single marker
      if (!this.show_info_window) {
        // A deliberate click, so bring the chosen device into view
        this.map.panTo(position);
        this.show_infowindow([id, position]);
        this.marker_clicked([id, position]);
      } else {
        this.map.setCenter(position);
        this.add_circle(position);
      }
    },
    validate_lat_lng(val) {
      return typeof val == "number" ? val : 0;
    },
    validate_id(val, index) {
      return val && val.toString() ? val.toString() : "un_" + index;
    },
    deviceTypeConfig(dev, idx) {
      let type = this.selected_region.status[dev].type[idx] || null;
      if (!type) return null;

      if (type.includes("Ground Mount")) {
        return this.device_types_config["Ground Mount"];
      } else if (type.includes("Pole Mount")) {
        return this.device_types_config["Pole Mount"];
      } else if (type.includes("Indoor")) {
        return this.device_types_config["Indoor"];
      }
      return null;
    },
    isValidLocation(elem) {
      return elem[this.options.lat] && elem[this.options.lng];
    },
    setHeatMap() {
      this.getMap((map) => {
        this.heatmap = new window.google.maps.visualization.HeatmapLayer({
          data: this.getPoints(),
          map: map,
        });
      });
    },
    getPoints() {
      return this.items.map((item) => {
        let latLng = new window.google.maps.LatLng(item[this.options.lat], item[this.options.lng]);
        return latLng;
      });
    },
  },
  computed: {
    route_start: function () {
      return this.start;
    },
    route_end: function () {
      return this.end;
    },
    show_info_window: function () {
      return this.no_info_window || false;
    },
    formatted_items: function () {
      return this.items
        ? this.items.map((item) => {
            return [
              new window.google.maps.LatLng(item[this.options.lat], item[this.options.lng]),
              item[this.options.id],
              item[this.options.type],
            ];
          })
        : null;
    },
    theme_background() {
      return this.$vuetify.theme.dark
        ? this.$vuetify.theme.themes.dark.backgroundLight
        : this.$vuetify.theme.themes.light.backgroundLight;
    },
    has_location() {
      if (this.start || this.end) return true;
      if (!this.items) return false;
      return this.items.some(this.isValidLocation);
    },
  },
  beforeDestroy() {
    let map = this.map;
    map &&
      map.data.forEach(function (feature) {
        map.data.remove(feature);
      });
  },
};
</script>
<style lang="scss" scoped>
.maps-container {
  position: relative;
  height: 100%;
  width: 100%;
}
#map {
  height: 100%;
  background: grey;
}
// Holds Vue-managed map children; visible content is moved into Google's DOM
.map-overlays {
  display: none;
}
.legends {
  position: absolute;
  z-index: 1;
  left: 5rem;
  top: 20px;
  right: 5rem;
  border-radius: 4px;
  div {
    margin-right: 5px;
  }
}
.info-message {
  position: absolute;
  z-index: 9;
  left: 5rem;
  top: 1rem;
  right: 5rem;
  border-radius: 4px;
  text-align: center;
  color: var(--v-vtred-base);
  span {
    background: #c6ced4;
  }
}
.slider-con {
  left: 1.5rem;
  top: 4rem;
  background: transparent;
  position: absolute;
  z-index: 9;
  height: 40vh;
}
.categories-div {
  background: var(--v-backgroundWhite-base);
  opacity: 0.9;
  position: absolute;
  z-index: 9;
  top: 6em;
  right: 1.5em;
  border-radius: 0.5em;
  padding: 0.5em;
  ::v-deep .v-label {
    font-size: 0.75em;
  }
  ::v-deep label {
    margin-bottom: 0;
  }
}
.group-list {
  min-width: 160px;
  max-width: 260px;
  .group-list-items {
    max-height: 200px;
    overflow-y: auto;
  }
  .group-list-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    margin-right: 4px;
    border-radius: 50%;
  }
  .group-list-item {
    cursor: pointer;
    padding: 2px 4px;
    border-radius: 4px;
    &:hover {
      background: var(--v-backgroundGrey-base, rgba(0, 0, 0, 0.06));
    }
  }
}
.cluster-div {
  background: var(--v-backgroundWhite-base);
  opacity: 0.9;
  position: absolute;
  z-index: 1;
  bottom: 0.75em;
  left: 1em;
  border-radius: 0.5em;
  padding: 0.35em;
  ::v-deep .v-label {
    font-size: 0.75em;
  }
  ::v-deep label {
    margin-bottom: 0;
  }
  ::v-deep .v-input {
    padding-top: 0;
  }
}
::v-deep .map-icon-label .map-icon {
  font-size: 24px;
  color: #ffffff;
  line-height: 48px;
  text-align: center;
  white-space: nowrap;
}
::v-deep .gm-style-iw-tc {
  display: none;
}

.center-on-map {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}
.cover-map {
  opacity: 0.1;
}
</style>
